import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { Dayjs, newDate } from '~/services/TimeService';

import PromotionalDiscountSummary from './PromotionalDiscountSummary';
import { DiscountSchedule, PromotionRoomType, PromotionalDiscount, PromotionalDiscountAccordionProps } from './types';

const DEFAULT_SCHEDULE: DiscountSchedule = {
  activePeriod: { from: null, to: null },
  travelPeriod: { from: null, to: null },
  type: 'sell',
};

const CHECKBOX_ICON = <CheckBoxOutlineBlank fontSize="small" />;
const CHECKBOX_CHECKED_ICON = <CheckBox fontSize="small" />;

function parseNumericValue(raw: string): number | null {
  if (raw === '') {
    return null;
  }
  const num = Number(raw);
  if (isNaN(num)) {
    return null;
  }
  return num;
}

function isValidPercentage(value: number | null): boolean {
  return value == null || (value >= 0 && value <= 100);
}

export default function PromotionalDiscountAccordion({
  index,
  value,
  onChange,
  onDelete,
  errors = {},
  roomTypes,
  isRoomTypesError,
}: PromotionalDiscountAccordionProps) {
  const [expanded, setExpanded] = useState(false);
  const [discountPercentError, setDiscountPercentError] = useState(false);
  const [luxPlusPercentError, setLuxPlusPercentError] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // The submitted value must drop room_type_ids while unscoped (the API rejects
  // unscoped-with-rooms), so the last picked set is kept here to restore.
  const [rememberedRoomTypeIds, setRememberedRoomTypeIds] = useState<Array<string> | undefined>(
    value.room_type_ids?.length ? value.room_type_ids : undefined,
  );

  const schedule = value.schedules?.[0] ?? DEFAULT_SCHEDULE;

  const activePeriodFrom = schedule.activePeriod.from ? newDate(schedule.activePeriod.from) : null;
  const activePeriodTo = schedule.activePeriod.to ? newDate(schedule.activePeriod.to) : null;
  const travelPeriodFrom = schedule.travelPeriod.from ? newDate(schedule.travelPeriod.from) : null;
  const travelPeriodTo = schedule.travelPeriod.to ? newDate(schedule.travelPeriod.to) : null;

  const updateField = useCallback(
    (patch: Partial<PromotionalDiscount>) => {
      onChange({ ...value, ...patch });
    },
    [value, onChange],
  );

  const handlePercentChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      field: 'discount_percent' | 'luxplus_discount_percent',
    ) => {
      const raw = event.target.value;
      const setError = field === 'discount_percent' ? setDiscountPercentError : setLuxPlusPercentError;

      if (raw === '') {
        updateField({ [field]: field === 'discount_percent' ? 0 : null });
        setError(false);
        return;
      }

      const num = Number(raw);
      if (!isNaN(num)) {
        updateField({ [field]: num });
        setError(!isValidPercentage(num));
      }
    },
    [updateField],
  );

  const handleNumericChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof PromotionalDiscount) => {
      updateField({ [field]: parseNumericValue(event.target.value) });
    },
    [updateField],
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onDelete();
    },
    [onDelete],
  );

  const selectedRoomTypes = useMemo(
    () => roomTypes.filter((roomType) => (value.room_type_ids ?? []).includes(roomType.id)),
    [roomTypes, value.room_type_ids],
  );

  const handleScopeModeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === 'room-type') {
        // An untouched switch to room type starts fully ticked, covering
        // everything currently sold rather than nothing.
        updateField({
          is_room_type_scoped: true,
          room_type_ids: rememberedRoomTypeIds?.length
            ? rememberedRoomTypeIds
            : roomTypes.map((roomType) => roomType.id),
        });
      } else {
        updateField({ is_room_type_scoped: false, room_type_ids: undefined });
      }
    },
    [updateField, rememberedRoomTypeIds, roomTypes],
  );

  const handleRoomTypesChange = useCallback(
    (_event: React.SyntheticEvent, newValue: Array<PromotionRoomType>) => {
      if (newValue.length === 0) {
        enqueueSnackbar('A promotion must apply to at least one room type — delete the promotion instead.', {
          variant: 'error',
        });
        return;
      }
      const roomTypeIds = newValue.map((roomType) => roomType.id);
      setRememberedRoomTypeIds(roomTypeIds);
      updateField({ room_type_ids: roomTypeIds });
    },
    [updateField, enqueueSnackbar],
  );

  const handleDateChange = useCallback(
    (periodKey: 'activePeriod' | 'travelPeriod', dateKey: 'from' | 'to', newValue: Dayjs | null) => {
      onChange({
        ...value,
        schedules: [
          {
            ...schedule,
            [periodKey]: {
              ...schedule[periodKey],
              [dateKey]: newValue ? newValue.format('YYYY-MM-DD') : null,
            },
          },
        ],
      });
    },
    [value, schedule, onChange],
  );

  const hasErrors = Object.values(errors).some(Boolean);
  const isExpanded = expanded || hasErrors;

  return (
    <Accordion expanded={isExpanded} onChange={() => setExpanded((prev) => !prev)} sx={{ bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <PromotionalDiscountSummary index={index} value={value} />
        </AccordionSummary>
        <IconButton color="error" size="small" onClick={handleDeleteClick}>
          <DeleteIcon />
        </IconButton>
      </Box>
      <AccordionDetails>
        <Typography variant="overline" color="primary" fontWeight="bold">
          Discount Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Discount Name"
              placeholder="e.g. Early Bird"
              value={value.discount_name || ''}
              onChange={(e) => updateField({ discount_name: e.target.value || null })}
              fullWidth
              error={!!errors.discountName}
              helperText={errors.discountName}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Discount %"
              type="number"
              value={value.discount_percent}
              onChange={(e) => handlePercentChange(e, 'discount_percent')}
              error={discountPercentError || !!errors.discountPercent}
              helperText={discountPercentError ? 'Value must be between 0 and 100' : errors.discountPercent}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Lux Plus %"
              type="number"
              value={value.luxplus_discount_percent ?? ''}
              onChange={(e) => handlePercentChange(e, 'luxplus_discount_percent')}
              error={luxPlusPercentError}
              helperText={luxPlusPercentError ? 'Value must be between 0 and 100' : undefined}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
          </Grid>
        </Grid>

        {isRoomTypesError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Unable to load room types, so the rooms this promotion applies to can't be changed.
          </Alert>
        )}
        {roomTypes.length === 0 && errors.roomTypeScope && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.roomTypeScope}
          </Alert>
        )}

        {roomTypes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="primary" fontWeight="bold">
              Applies To
            </Typography>
            <RadioGroup
              row
              value={value.is_room_type_scoped ? 'room-type' : 'rate-plan'}
              onChange={handleScopeModeChange}
            >
              <FormControlLabel value="rate-plan" control={<Radio />} label="Apply to rate plan" />
              <FormControlLabel value="room-type" control={<Radio />} label="Apply to room type" />
            </RadioGroup>

            {value.is_room_type_scoped && (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={roomTypes}
                value={selectedRoomTypes}
                getOptionLabel={(roomType) => roomType.name}
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                onChange={handleRoomTypesChange}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox
                        icon={CHECKBOX_ICON}
                        checkedIcon={CHECKBOX_CHECKED_ICON}
                        checked={selected}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rooms"
                    placeholder="Select rooms"
                    error={!!errors.roomTypeScope}
                    helperText={errors.roomTypeScope}
                  />
                )}
              />
            )}
          </Box>
        )}

        <Typography variant="overline" color="primary" fontWeight="bold">
          Stay & Arrival Options
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Min LOS"
              type="number"
              value={value.min_los ?? ''}
              onChange={(e) => handleNumericChange(e, 'min_los')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Max LOS"
              type="number"
              value={value.max_los ?? ''}
              onChange={(e) => handleNumericChange(e, 'max_los')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Min Days to Arrival"
              type="number"
              value={value.min_days_to_arrival ?? ''}
              onChange={(e) => handleNumericChange(e, 'min_days_to_arrival')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Max Days to Arrival"
              type="number"
              value={value.max_days_to_arrival ?? ''}
              onChange={(e) => handleNumericChange(e, 'max_days_to_arrival')}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Date Periods */}
        <Typography variant="overline" color="primary" fontWeight="bold">
          Date Periods
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Active Period From"
              format="DD-MM-YYYY"
              value={activePeriodFrom}
              maxDate={activePeriodTo ?? undefined}
              onChange={(date) => handleDateChange('activePeriod', 'from', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.activePeriodFrom,
                  helperText: errors.activePeriodFrom,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Active Period To"
              format="DD-MM-YYYY"
              value={activePeriodTo}
              minDate={activePeriodFrom ?? undefined}
              onChange={(date) => handleDateChange('activePeriod', 'to', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.activePeriodTo,
                  helperText: errors.activePeriodTo,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Travel Period From"
              format="DD-MM-YYYY"
              value={travelPeriodFrom}
              maxDate={travelPeriodTo?.subtract(1, 'day') ?? undefined}
              onChange={(date) => handleDateChange('travelPeriod', 'from', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.travelPeriodFrom,
                  helperText: errors.travelPeriodFrom,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Travel Period To"
              format="DD-MM-YYYY"
              value={travelPeriodTo}
              minDate={travelPeriodFrom?.add(1, 'day') ?? undefined}
              onChange={(date) => handleDateChange('travelPeriod', 'to', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.travelPeriodTo,
                  helperText: errors.travelPeriodTo,
                },
              }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
